import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult
} from 'react-beautiful-dnd';
import * as Yup from 'yup';
import styled from 'styled-components';
import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import {
  Add as AddIcon,
  Close as CloseIcon,
  DragIndicator as DragIndicatorIcon,
  Edit as EditIcon,
  HelpOutline as HelpOutlineIcon
} from '@material-ui/icons';

import { FetchError } from '../../../common/api/api';
import { reorder } from '../../../common/reorder.helper';
import { useTimeout } from '../../../common/timeout.hook';
import WrappedTableCell from '../../../common/WrappedTableCell';
import ConfirmDialog from '../../../common/dialogs/ConfirmDialog';
import InlineForm from '../../../common/inline/InlineForm';
import InlineFormik from '../../../common/inline/InlineFormik';
import InlineTextField from '../../../common/inline/InlineTextField';
import { Role } from '../../../user/User.interface';
import { UserContext } from '../../../user/User.context';
import { FileDto } from '../../documents/Document.interface';
import DocumentTemplateDialog from '../../documents/components/DocumentTemplateDialog';
import {
  JiraConnect,
  JiraConnectConfigExportPreset
} from '../JiraConnect.interface';
import { useUpdateJiraConnect } from '../JiraConnect.hooks';
import JiraReferenceDialog from './JiraReferenceDialog';

const StyledDialogTitle = styled(DialogTitle)`
  display: flex;
  align-items: center;
  .title {
    flex: 1;
  }
`;

const StyledAlert = styled(Alert)`
  padding: 0px;
  background: none;
  .MuiAlert-icon {
    padding-bottom: 0px;
  }
`;

const StyledTableRow = styled(TableRow)`
  background-color: #fff;
  .drag {
    padding: 2px;
  }
  .actions {
    padding-top: 2px;
    padding-bottom: 2px;
  }
  .no-content {
    font-style: italic;
  }
`;

export interface JiraTemplateConfigButtonProps {
  title?: string;
  disabled?: boolean;
  buttonLabel?: string;
  jiraConnect: JiraConnect | undefined;
  setJiraConnect: (jiraConnect: JiraConnect | undefined) => void;
}

const JiraTemplateConfigButton: React.FC<JiraTemplateConfigButtonProps> = (
  props: JiraTemplateConfigButtonProps
) => {
  const {
    disabled: disabledProp = false,
    title = 'Configure Templates',
    buttonLabel = 'Settings',
    jiraConnect,
    setJiraConnect
  } = props;
  const maxTemplates: number = 10;
  const { user } = useContext(UserContext);
  const [open, setOpen] = useState<boolean>(false);
  const [didUpdate, setDidUpdate] = useState<boolean>(false);
  const [openReferenceDialog, setOpenReferenceDialog] = useState<boolean>(
    false
  );
  const [removeId, setRemoveId] = useState<number | undefined>();
  const [error, setError] = useState<FetchError | undefined>();
  const [presets, setPresets] = useState<JiraConnectConfigExportPreset[]>([]);
  const [selectedPreset, setSelectedPreset] = useState<
    JiraConnectConfigExportPreset | undefined
  >();
  const [draggingHeight, setDraggingHeight] = useState<number | undefined>();
  const [
    { data, isLoading, error: updateError },
    update
  ] = useUpdateJiraConnect();
  const hasMaxTemplates: boolean = presets.length >= maxTemplates;

  const disabled: boolean = useMemo(() => {
    return (
      disabledProp ||
      !user?.roles?.some(
        (role: Role) => role.name && ['Admin', 'SiteAdmin'].includes(role.name)
      )
    );
  }, [disabledProp]);

  const selectedFile: FileDto | undefined = useMemo(() => {
    if (!jiraConnect || !selectedPreset?.fileId) {
      return undefined;
    }
    const file: FileDto | undefined = jiraConnect.files?.find(
      (file: FileDto) => file.id === selectedPreset.fileId
    );
    return file;
  }, [jiraConnect, selectedPreset]);

  const updateJiraConnect = (
    newPresets: JiraConnectConfigExportPreset[] = presets
  ) => {
    if (!jiraConnect) {
      return;
    }
    if (!jiraConnect.config) {
      jiraConnect.config = { exportPresets: newPresets };
    }
    jiraConnect.config.exportPresets = newPresets;
    setDidUpdate(false);
    update({ ...jiraConnect });
  };

  const setAndUpdatePresets = (presets: JiraConnectConfigExportPreset[]) => {
    setPresets(presets);
    updateJiraConnect(presets);
  };

  const getNextId = (): number => {
    let id: number = 1;
    const presetIds: number[] = presets
      .map((preset: JiraConnectConfigExportPreset) => preset.id)
      .sort((a: number, b: number) => a - b);
    for (const presetId of presetIds) {
      if (presetId !== id) {
        break;
      }
      id++;
    }
    return id;
  };

  const getPresetIndex = (id: number): number => {
    return presets.findIndex(
      (preset: JiraConnectConfigExportPreset) => preset.id === id
    );
  };

  const updateItem = (
    id: number,
    newPreset: Partial<JiraConnectConfigExportPreset>
  ): JiraConnectConfigExportPreset => {
    const index: number = getPresetIndex(id);
    const newPresets: JiraConnectConfigExportPreset[] = [...presets];
    const updatedPreset: JiraConnectConfigExportPreset =
      index > -1
        ? { ...newPresets[index], ...newPreset }
        : { id, name: '', ...newPreset };
    if (index > -1) {
      newPresets.splice(index, 1, updatedPreset);
    } else {
      newPresets.push(updatedPreset);
    }
    setAndUpdatePresets(newPresets);
    return updatedPreset;
  };

  const removeItem = (id: number) => {
    const index: number = getPresetIndex(id);
    if (index > -1) {
      const newPresets: JiraConnectConfigExportPreset[] = [...presets];
      newPresets.splice(index, 1);
      setAndUpdatePresets(newPresets);
    }
  };

  const moveItem = (sourceIndex: number, destinationIndex: number) => {
    const newPresets: JiraConnectConfigExportPreset[] = reorder(
      presets,
      sourceIndex,
      destinationIndex
    );
    setAndUpdatePresets(newPresets);
  };

  const handleDragEnd = (result: DropResult) => {
    const { source, destination } = result;
    // dropped outside the list
    if (!destination) {
      return;
    }
    moveItem(source.index, destination.index);
  };

  useEffect(() => {
    setDidUpdate(false);
  }, [open]);

  useEffect(() => {
    if (data) {
      setDidUpdate(true);
      setJiraConnect(data);
    }
  }, [data]);

  useEffect(() => {
    if (jiraConnect) {
      setPresets(jiraConnect.config?.exportPresets || []);
    }
  }, [jiraConnect]);

  useEffect(() => {
    setError(updateError);
  }, [updateError]);

  return (
    <>
      <Button
        color='secondary'
        variant='outlined'
        disabled={disabled}
        style={{ marginRight: 10 }}
        onClick={() => setOpen(true)}
      >
        {buttonLabel}
      </Button>

      <Dialog open={open} maxWidth='md' onClose={() => setOpen(false)}>
        <StyledDialogTitle disableTypography>
          <Typography variant='h6' className='title'>
            {title}
          </Typography>
          <Button
            size='small'
            color='primary'
            variant='outlined'
            endIcon={<HelpOutlineIcon />}
            onClick={() => setOpenReferenceDialog(true)}
          >
            Reference
          </Button>
          <JiraReferenceDialog
            open={openReferenceDialog}
            onClose={() => setOpenReferenceDialog(false)}
          />
        </StyledDialogTitle>
        <Box mx={3} my={1}>
          <DialogContentText>
            Setup document template presets.
          </DialogContentText>
        </Box>
        <TableContainer
          style={
            typeof draggingHeight === 'number'
              ? { paddingBottom: draggingHeight }
              : undefined
          }
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={1} />
                <TableCell>Preset Name</TableCell>
                <TableCell>Document</TableCell>
                <TableCell width={1} />
              </TableRow>
            </TableHead>
            <DragDropContext
              onDragEnd={handleDragEnd}
              key={`templates-${presets.length}`}
            >
              <Droppable droppableId='templates'>
                {(provided, _snapshot) => (
                  <TableBody ref={provided.innerRef}>
                    {isLoading && (
                      <TableRow>
                        <TableCell colSpan={5} padding='none'>
                          <LinearProgress />
                        </TableCell>
                      </TableRow>
                    )}
                    {jiraConnect && presets.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Typography variant='body1' color='textSecondary'>
                            No templates available.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {jiraConnect &&
                      presets.map(
                        (
                          preset: JiraConnectConfigExportPreset,
                          index: number
                        ) => (
                          <JiraTemplateConfigRow
                            key={preset.id}
                            index={index}
                            preset={preset}
                            jiraConnect={jiraConnect}
                            setDraggingHeight={setDraggingHeight}
                            onEdit={() => setSelectedPreset(preset)}
                            onRemove={() => setRemoveId(preset.id)}
                          />
                        )
                      )}
                  </TableBody>
                )}
              </Droppable>
            </DragDropContext>
          </Table>
        </TableContainer>

        <Collapse in={!!error} style={{ minHeight: 'unset' }}>
          <Box mx={3} mt={1}>
            <Alert severity='error'>
              {error?.message || 'An error occurred.'}
            </Alert>
          </Box>
        </Collapse>

        <DialogActions>
          {didUpdate && !isLoading && !error && (
            <>
              <StyledAlert severity='success'>
                Configuration updated.
              </StyledAlert>
              <div style={{ flex: 1 }} />
            </>
          )}
          <Tooltip
            arrow
            title={
              hasMaxTemplates ? `Maximum of ${maxTemplates} templates only` : ''
            }
          >
            <span>
              <Button onClick={() => setOpen(false)}>
                Cancel
              </Button>
              &nbsp;
              <Button
                color='primary'
                variant='contained'
                startIcon={<AddIcon />}
                disabled={hasMaxTemplates}
                onClick={() => setSelectedPreset({ id: getNextId(), name: '' })}
              >
                Add Template
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>

      {selectedPreset && (
        <InlineFormik
          isSubmitting={isLoading}
          initialValues={{ name: selectedPreset.name }}
          validationSchema={Yup.object({
            name: Yup.string().required('Template name is required.')
          })}
          onSubmit={(form: Partial<JiraConnectConfigExportPreset>) => {
            setSelectedPreset({ ...selectedPreset, ...form });
          }}
        >
          {({ isValid }) => (
            <DocumentTemplateDialog
              hideUnsetButton
              file={selectedFile}
              open={!!selectedPreset}
              confirmLabel='Use as template'
              disableConfirmButton={!isValid}
              setOpen={(open: boolean) => !open && setSelectedPreset(undefined)}
              onSelectFile={(file?: FileDto) => {
                updateItem(selectedPreset.id, {
                  ...selectedPreset,
                  fileId: file?.id || null
                });
                setSelectedPreset(undefined);
              }}
              content={
                <Box mb={2}>
                  <InlineForm>
                    <InlineTextField
                      name='name'
                      label='Set Template Name'
                      placeholder='Provide a name for the template'

                    />
                  </InlineForm>
                </Box>
              }
            />
          )}
        </InlineFormik>
      )}

      <ConfirmDialog
        open={!!removeId}
        confirmLabel='Confirm'
        title='Remove Template'
        message='Are you sure you want to remove this template?'
        onCancel={() => setRemoveId(undefined)}
        onConfirm={() => {
          removeId && removeItem(removeId);
          setRemoveId(undefined);
        }}
      />
    </>
  );
};

export default JiraTemplateConfigButton;

export interface JiraTemplateConfigRowProps {
  index: number;
  jiraConnect: JiraConnect;
  preset: JiraConnectConfigExportPreset;
  onEdit?: () => void;
  onRemove?: () => void;
  setDraggingHeight?: (height: number | undefined) => void;
}

export const JiraTemplateConfigRow: React.FC<JiraTemplateConfigRowProps> = (
  props: JiraTemplateConfigRowProps
) => {
  const {
    index,
    preset,
    jiraConnect,
    onEdit,
    onRemove,
    setDraggingHeight
  } = props;
  const [debounceDrag] = useTimeout();
  const elementRef = useRef<HTMLElement | null>(null);
  const [draggingHeight, setDraggingHeightState] = useState<
    number | undefined
  >();

  const file: FileDto | undefined = useMemo(() => {
    return jiraConnect.files?.find(
      (file: FileDto) => file.id === preset.fileId
    );
  }, [jiraConnect, preset]);

  useEffect(() => {
    setDraggingHeight?.(draggingHeight);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draggingHeight]);

  return (
    <Draggable index={index} draggableId={`row-${preset.id}`}>
      {(provided, snapshot) => {
        debounceDrag(() => {
          const height: number | undefined = elementRef.current?.offsetHeight;
          const isDragging: boolean = typeof draggingHeight === 'number';
          isDragging !== snapshot.isDragging &&
            setDraggingHeightState(snapshot.isDragging ? height : undefined);
        });

        return (
          <StyledTableRow
            {...provided.draggableProps}
            ref={(ref: HTMLElement | null) => {
              elementRef.current = ref;
              provided.innerRef(ref);
            }}
          >
            <TableCell className='drag'>
              <IconButton {...provided.dragHandleProps}>
                <DragIndicatorIcon />
              </IconButton>
            </TableCell>
            <WrappedTableCell>
              {preset.name || (
                <Typography
                  variant='body2'
                  color='textSecondary'
                  className='no-content'
                >
                  Untitled
                </Typography>
              )}
            </WrappedTableCell>
            <WrappedTableCell>
              {file?.path ? (
                <Link to={`/portal/documents/file/task/${file.taskId}`}>
                  {file.path}
                </Link>
              ) : (
                <Typography
                  variant='body2'
                  color='textSecondary'
                  className='no-content'
                >
                  No template
                </Typography>
              )}
            </WrappedTableCell>
            <TableCell className='actions'>
              <IconButton onClick={onEdit}>
                <EditIcon />
              </IconButton>
              <IconButton onClick={onRemove}>
                <CloseIcon />
              </IconButton>
            </TableCell>
          </StyledTableRow>
        );
      }}
    </Draggable>
  );
};
